import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { mq } from '@hangar31/rc-layout';

export const HeroStripStyled = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    align-items: center;
    max-width: 72rem;
    margin: 1.6rem auto 3.2rem;
    padding: 0 2rem;

    text-align: center;

    h1 {
      margin: 0;

      color: ${theme.colors.ink};
      font-weight: 700;
      font-size: 3.6rem;
      line-height: 1.15;
      letter-spacing: -0.01em;
    }
    p {
      margin: 0;

      color: ${theme.colors.grayDark};
      font-size: 1.7rem;
      line-height: 1.4;
    }
  `,
  mq({
    h1: { fontSize: ['2.8rem', undefined, '3.6rem'] },
    marginTop: ['2rem', undefined, '4rem'],
  })
);

export const AudiencePillStyled = styled.span(
  ({ theme }) => css`
    align-self: flex-start;

    padding: 0.4rem 1rem;

    color: ${theme.colors.primary};
    font-weight: 600;
    font-size: 1.2rem;
    letter-spacing: 0.02em;
    text-transform: uppercase;

    background-color: ${theme.colors.primaryLight};
    border-radius: 999px;
  `
);

export const RecommendedPillStyled = styled.span(
  ({ theme }) => css`
    position: absolute;
    top: -1.2rem;
    left: 50%;

    padding: 0.4rem 1.4rem;

    color: ${theme.colors.white};
    font-weight: 700;
    font-size: 1.2rem;
    letter-spacing: 0.04em;
    white-space: nowrap;
    text-transform: uppercase;

    background-color: ${theme.colors.highlight};
    border-radius: 999px;
    box-shadow: ${theme.shadows.sm};
    transform: translateX(-50%);
  `
);

export const ValueLineStyled = styled.p(
  ({ theme }) => css`
    margin: 0 0 1.6rem;

    color: ${theme.colors.ink};
    font-weight: 500;
    font-size: 1.6rem;
    font-style: normal;
    line-height: 1.35;
    text-align: left;
  `
);

export const PriceMetaStyled = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: 0.8rem;
    align-items: center;
    margin: -0.4rem 0 1.2rem;

    font-size: 1.3rem;

    span {
      color: ${theme.colors.grayDark};
    }
    strong {
      color: ${theme.colors.highlight};
      font-weight: 700;
    }
  `
);

export const PickerFooterStyled = styled.p(
  ({ theme }) => css`
    margin: 2.4rem auto 0;

    color: ${theme.colors.grayDark};
    font-size: 1.5rem;
    font-style: italic;
    text-align: center;
  `
);

export const TrustStripStyled = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    gap: 1.6rem 3.2rem;
    justify-content: center;
    align-items: center;
    max-width: 64rem;
    margin: 3.2rem auto 0;
    padding: 1.6rem 2.4rem;

    color: ${theme.colors.grayDark};
    font-size: 1.4rem;

    background-color: ${theme.colors.whiteDark};
    border: 1px solid ${theme.colors.border};
    border-radius: 999px;

    span {
      display: flex;
      gap: 0.6rem;
      align-items: center;
    }
    svg {
      color: ${theme.colors.success};
    }
  `
);

export const BillingPageStyled = styled.div(
  () => css`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    margin: 0 0 4rem;
  `
);

export const StatusBannerStyled = styled.div<{
  tone?: 'warning' | 'danger' | 'info';
}>(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    padding: 1.6rem 2rem;

    font-size: 1.45rem;

    background-color: ${theme.colors.warningLight};
    border: 1px solid ${theme.colors.warning};
    border-radius: 1rem;

    strong {
      color: ${theme.colors.warning};
      font-weight: 700;
    }
    button {
      align-self: flex-start;
      margin-top: 0.6rem;
      padding: 0.6rem 1.4rem;

      color: ${theme.colors.ink};
      font-weight: 600;
      font-size: 1.4rem;

      background: ${theme.colors.white};
      border: 1px solid ${theme.colors.border};
      border-radius: 0.6rem;
    }
  `,
  ({ theme, tone }) =>
    tone === 'danger' &&
    css`
      background-color: ${theme.colors.errorLight};
      border-color: ${theme.colors.error};
      strong {
        color: ${theme.colors.error};
      }
    `,
  ({ theme, tone }) =>
    tone === 'info' &&
    css`
      background-color: ${theme.colors.primaryLight};
      border-color: ${theme.colors.clickableLight};
      strong {
        color: ${theme.colors.primary};
      }
    `
);

export const CurrentPlanCardStyled = styled.section(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    padding: 2.4rem;

    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.border};
    border-radius: 1.2rem;
    box-shadow: ${theme.shadows.sm};

    h2 {
      margin: 0;

      color: ${theme.colors.grayMed};
      font-weight: 600;
      font-size: 1.2rem;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }
    .plan-name {
      color: ${theme.colors.ink};
      font-weight: 700;
      font-size: 2.4rem;
    }
    .plan-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 0.4rem 1.2rem;

      color: ${theme.colors.grayDark};
      font-size: 1.4rem;
    }
    .plan-meta strong {
      color: ${theme.colors.ink};
      font-weight: 600;
    }
    .actions {
      display: flex;
      flex-wrap: wrap;
      gap: 0.8rem;
      margin-top: 0.8rem;
    }
    .actions button,
    .actions a {
      padding: 0.9rem 1.6rem;

      color: ${theme.colors.ink};
      font-weight: 600;
      font-size: 1.4rem;
      text-decoration: none;

      background: ${theme.colors.white};
      border: 1px solid ${theme.colors.border};
      border-radius: 0.6rem;
      cursor: pointer;
    }
    .actions .primary {
      color: ${theme.colors.white};

      background: ${theme.colors.clickable};
      border-color: ${theme.colors.clickable};
    }
    .actions .danger {
      color: ${theme.colors.error};
    }
    .actions .danger:hover {
      background: ${theme.colors.errorLight};
    }
  `
);

export const UsageMeterStyled = styled.div<{ tone: 'ok' | 'warn' | 'over' }>(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    margin-top: 0.4rem;
    padding-top: 1.6rem;

    border-top: 1px solid ${theme.colors.border};

    .usage-head {
      display: flex;
      gap: 1.2rem;
      justify-content: space-between;
      align-items: baseline;

      color: ${theme.colors.grayDark};
      font-size: 1.4rem;
    }
    .usage-head strong {
      color: ${theme.colors.ink};
      font-weight: 700;
    }
    .usage-track {
      position: relative;
      overflow: hidden;

      height: 0.8rem;

      background: ${theme.colors.whiteDark};
      border-radius: 999px;
    }
    .usage-fill {
      height: 100%;

      border-radius: 999px;

      transition: width 0.3s ease;
    }
    .usage-note {
      color: ${theme.colors.grayMed};
      font-size: 1.3rem;
      line-height: 1.45;
    }
    .usage-upgrade {
      display: flex;
      flex-wrap: wrap;
      gap: 0.8rem 1.2rem;
      align-items: center;
      justify-content: space-between;
      margin-top: 0.4rem;
      padding: 1rem 1.2rem;

      background: ${theme.colors.primaryLight};
      border-radius: 0.8rem;
    }
    .usage-upgrade span {
      flex: 1;
      min-width: 20rem;

      color: ${theme.colors.ink};
      font-size: 1.35rem;
      line-height: 1.4;
    }
    .usage-upgrade strong {
      color: ${theme.colors.primary};
      font-weight: 700;
    }
    .usage-upgrade button {
      flex-shrink: 0;
      padding: 0.8rem 1.4rem;

      color: ${theme.colors.white};
      font-weight: 600;
      font-size: 1.35rem;

      background: ${theme.colors.clickable};
      border: 1px solid ${theme.colors.clickable};
      border-radius: 0.6rem;
      cursor: pointer;
    }
  `,
  ({ theme, tone }) =>
    tone === 'ok' &&
    css`
      .usage-fill {
        background: ${theme.colors.success};
      }
    `,
  ({ theme, tone }) =>
    tone === 'warn' &&
    css`
      .usage-fill {
        background: ${theme.colors.warning};
      }
      .usage-note {
        color: ${theme.colors.warning};
      }
    `,
  ({ theme, tone }) =>
    tone === 'over' &&
    css`
      .usage-fill {
        background: ${theme.colors.error};
      }
      .usage-note {
        color: ${theme.colors.error};
        font-weight: 600;
      }
    `
);

export const ManageStripeFooterStyled = styled.div(
  ({ theme }) => css`
    margin-top: 1.2rem;

    color: ${theme.colors.grayMed};
    font-size: 1.3rem;
    text-align: center;

    a {
      color: ${theme.colors.grayDark};
      text-decoration: underline;
    }
  `
);

export const ModalBackdropStyled = styled.div(
  () => css`
    position: fixed;
    inset: 0;

    z-index: 1000;

    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem;

    background: rgba(15, 11, 26, 0.55);
  `
);

export const ModalCardStyled = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    width: 100%;
    max-width: 48rem;
    padding: 2.4rem;

    background: ${theme.colors.white};
    border-radius: 1.2rem;
    box-shadow: ${theme.shadows.lg};

    h3 {
      margin: 0;

      color: ${theme.colors.ink};
      font-weight: 700;
      font-size: 2rem;
    }
    p {
      margin: 0;

      color: ${theme.colors.grayDark};
      font-size: 1.4rem;
      line-height: 1.5;
    }
    label {
      display: flex;
      flex-direction: column;
      gap: 0.4rem;

      color: ${theme.colors.ink};
      font-weight: 600;
      font-size: 1.3rem;
    }
    select,
    textarea {
      padding: 0.8rem 1rem;

      color: ${theme.colors.ink};
      font-weight: 400;
      font-size: 1.4rem;

      background: ${theme.colors.white};
      border: 1px solid ${theme.colors.border};
      border-radius: 0.6rem;
    }
    textarea {
      min-height: 8rem;

      resize: vertical;
    }
    p.hint {
      margin: -0.6rem 0 0;

      color: ${theme.colors.error};
      font-size: 1.3rem;
    }
    .actions {
      display: flex;
      gap: 0.8rem;
      justify-content: flex-end;
      margin-top: 0.8rem;
    }
    .actions button {
      padding: 0.9rem 1.6rem;

      font-weight: 600;

      font-size: 1.4rem;

      border-radius: 0.6rem;
      cursor: pointer;
    }
    .actions .keep {
      color: ${theme.colors.white};

      background: ${theme.colors.clickable};
      border: 1px solid ${theme.colors.clickable};
    }
    .actions .confirm {
      color: ${theme.colors.error};

      background: ${theme.colors.white};
      border: 1px solid ${theme.colors.border};
    }
  `
);

export const FaqSectionStyled = styled.section(
  ({ theme }) => css`
    max-width: 72rem;
    margin: 6.4rem auto 4rem;
    padding: 0 2rem;

    h2 {
      margin: 0 0 2.4rem;

      color: ${theme.colors.ink};
      font-weight: 700;
      font-size: 2.4rem;
      letter-spacing: -0.01em;
      text-align: center;
    }
  `
);

export const FaqItemStyled = styled.details(
  ({ theme }) => css`
    padding: 1.6rem 2rem;

    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.border};
    border-radius: 1rem;

    transition:
      border-color 0.18s ease,
      box-shadow 0.18s ease;

    & + & {
      margin-top: 1rem;
    }

    summary {
      display: flex;
      gap: 1.2rem;
      justify-content: space-between;
      align-items: center;

      color: ${theme.colors.ink};
      font-weight: 600;
      font-size: 1.6rem;

      list-style: none;
      cursor: pointer;
    }
    summary::-webkit-details-marker {
      display: none;
    }
    summary::after {
      flex-shrink: 0;

      color: ${theme.colors.grayMed};
      font-weight: 700;
      font-size: 1.4rem;

      transition: transform 0.18s ease;
      content: '+';
    }
    &[open] summary::after {
      transform: rotate(45deg);
    }

    p {
      margin: 1rem 0 0;

      color: ${theme.colors.grayDark};
      font-size: 1.45rem;
      line-height: 1.55;
    }

    &:hover {
      border-color: ${theme.colors.clickableLight};
    }
    &[open] {
      border-color: ${theme.colors.clickable};
      box-shadow: ${theme.shadows.sm};
    }
  `
);

export const PlanStyled = styled.article<{
  popular?: boolean;
  selected?: boolean;
}>(
  ({ theme }) => css`
    position: relative;

    display: flex;
    flex-direction: column;
    margin: 1.2rem 0 0;
    padding: 2.8rem 2.4rem 2.4rem;

    color: ${theme.colors.ink};
    text-align: left;

    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.border};
    border-radius: 1.6rem;
    box-shadow: ${theme.shadows.sm};

    transition:
      transform 0.18s ease,
      box-shadow 0.18s ease,
      border-color 0.18s ease;

    h1 {
      margin: 1.2rem 0 0;

      color: ${theme.colors.ink};
      font-weight: 600;
      font-size: 2.2rem;
    }
    h2 {
      margin: 0.4rem 0 0;

      color: ${theme.colors.ink};
      font-weight: 700;
      font-size: 3.6rem;
      letter-spacing: -0.02em;
      white-space: pre;
    }
    h2 small {
      color: ${theme.colors.grayDark};
      font-weight: 500;
      font-size: 1.4rem;
    }
    ul {
      display: flex;
      flex: 1;
      flex-direction: column;
      gap: 1rem;
      align-items: flex-start;
      margin: 0 0 2rem;
      padding: 0 0 0 0.4rem;

      list-style: none;
    }
    li {
      position: relative;

      padding-left: 2.2rem;

      color: ${theme.colors.ink};
      font-size: 1.45rem;
      line-height: 1.4;
    }
    li::before {
      position: absolute;
      top: 0.2rem;
      left: 0;

      color: ${theme.colors.success};
      font-weight: 700;
      content: '✓';
    }
    button {
      width: 100%;
      padding: 1.2rem;

      font-weight: 600;

      font-size: 1.5rem;

      border-radius: 0.8rem;
    }
    aside {
      margin-top: 1.2rem;

      color: ${theme.colors.grayMed};
      font-size: 1.2rem;
    }

    &:hover {
      border-color: ${theme.colors.clickableLight};
      box-shadow: ${theme.shadows.md};
    }
  `,
  ({ selected, theme }) =>
    selected &&
    css`
      border-color: ${theme.colors.clickable};
      box-shadow:
        0 0 0 2px ${theme.colors.clickable},
        ${theme.shadows.md};
      button {
        color: ${theme.colors.clickable};

        background: ${theme.colors.primaryLight};
        border: 1px solid ${theme.colors.clickable};

        pointer-events: none;
      }
    `,
  ({ popular, theme }) =>
    popular &&
    css`
      margin-top: 0;
      padding-top: 3.2rem;

      border-color: ${theme.colors.highlight};
      box-shadow: ${theme.shadows.md};
      transform: translateY(-1.2rem);

      &::before {
        position: absolute;
        top: 0;
        right: 0;
        left: 0;

        height: 0.4rem;

        background: linear-gradient(
          90deg,
          ${theme.colors.clickable},
          ${theme.colors.highlight}
        );
        border-radius: 1.6rem 1.6rem 0 0;
        content: '';
      }
      &:hover {
        border-color: ${theme.colors.highlight};
      }
    `,
  mq({
    h2: { fontSize: ['3rem', undefined, '3.6rem'] },
  })
);
